/* eslint-disable indent */
/* eslint-disable no-unused-vars */
/* eslint-disable quotes */
import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import "./style.scss";
import {
  Row,
  Col,
  Button,
  message,
  Card,
  Space,
  Typography,
  Tag,
  Upload,
  Divider,
} from "antd";
import API from "../../../api";
import urls from "../../../api/urls";
import {
  UploadOutlined,
  ScanOutlined,
  FileImageOutlined,
  DeleteOutlined,
  ArrowLeftOutlined,
} from "@ant-design/icons";

const {
  DRS_DETAILS,
  DRS_UPLOAD_POD,
  DRS_POD_UPDATE_SHIPMENT_LIST,
  DRS_UPDATE_POD,
} = urls;

const CpDrsPodUpload = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  
  // DRS Level POD upload state
  const [drsShipmentList, setDrsShipmentList] = useState([]);
  const [excludedShipments, setExcludedShipments] = useState([]);
  const [pdfPages, setPdfPages] = useState(0);
  const [pageFiles, setPageFiles] = useState({}); // {pageNumber: {file: File, url: string, type: 'upload'|'scan'}}
  const [drsLevelLoading, setDrsLevelLoading] = useState(false);
  const [scannerLoading, setScannerLoading] = useState({});
  const [drsDetails, setDrsDetails] = useState({});
  const [loading, setLoading] = useState(false);

  // DRS Level POD Upload Functions
  const fetchDrsShipmentList = async () => {
    try {
      setDrsLevelLoading(true);
      const response = await API.Api(
        DRS_POD_UPDATE_SHIPMENT_LIST + `?drsNumber=${id}`
      ).get();

      if (response.status && response.response?.data) {
        const { data, pdfPages: pages } = response.response.data;
        setDrsShipmentList(data || []);
        setPdfPages(pages || 1);
        setExcludedShipments([]);
        setPageFiles({});
      } else {
        message.error("Failed to fetch shipment list");
      }
    } catch (error) {
      console.error("Error fetching DRS shipment list:", error);
      message.error("Error fetching shipment list");
    } finally {
      setDrsLevelLoading(false);
    }
  };

  const getDrsDetails = async () => {
    try {
      setLoading(true);
      const { response: { data: { data } = {} } = {} } =
        (await API.Api(`${DRS_DETAILS}/${id}`).get()) || {};

      if (data) {
        const { drsDetails } = data;
        setDrsDetails(drsDetails);
      }
    } catch (error) {
      console.error("Error fetching DRS details:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleExcludeShipment = (shipment) => {
    setExcludedShipments((prev) => [...prev, shipment.cAWB_No]);
  };

  const handleIncludeShipment = (shipment) => {
    setExcludedShipments((prev) =>
      prev.filter((awb) => awb !== shipment.cAWB_No)
    );
  };

  const handlePodUpload = async (files, record) => {
    try {
      // Array to store all uploaded image URLs
      const uploadedUrls = [];

      // Upload each file one by one
      for (const file of files) {
        // Create form data according to the cURL format
        const formData = new FormData();
        const fileName = `${record.cAWB_No}.${file.name.split(".").pop().toLowerCase()}`;

        // Append file with the custom filename as the third parameter
        formData.append("files", file, fileName);
        formData.append("path", "/drs_pod");
        formData.append("fileType", file.type);
        formData.append("filename", fileName);

        // Call API to upload POD
        const response = await API.Api(DRS_UPLOAD_POD).post(formData);

        if (response.status) {
          // Extract URL from response and add to array
          const uploadedImageUrl = response?.response?.data?.data?.[0]?.url;
          if (uploadedImageUrl) {
            uploadedUrls.push(uploadedImageUrl);
          }
        } else {
          // Extract error message from response object
          const errorMsg =
            response?.response?.data?.message || "Failed to upload POD image";
          message.error(errorMsg);
        }
      }

      if (uploadedUrls.length > 0) {
        message.success(
          `Successfully uploaded ${uploadedUrls.length} image(s)`
        );
        return uploadedUrls;
      } else {
        message.error("Failed to upload any images");
        return null;
      }
    } catch (error) {
      console.error("Error uploading POD:", error);
      message.error(error?.message || "Error uploading POD images");
      return null;
    }
  };

  const handleFileUpload = async (file, pageNumber) => {
    try {
      // Create a temporary record for file naming
      const tempRecord = { cAWB_No: `DRS-${id}-Page-${pageNumber}` };

      // Upload the file using existing mechanism
      const uploadedUrls = await handlePodUpload([file], tempRecord);

      if (uploadedUrls && uploadedUrls.length > 0) {
        setPageFiles((prev) => ({
          ...prev,
          [pageNumber]: {
            file,
            url: uploadedUrls[0],
            type: "upload",
            name: file.name,
          },
        }));
        message.success(`File uploaded for page ${pageNumber}`);
      }
    } catch (error) {
      console.error("Error uploading file:", error);
      message.error("Failed to upload file");
    }
  };

  const handleRemovePageFile = (pageNumber) => {
    setPageFiles((prev) => {
      const newPageFiles = { ...prev };
      delete newPageFiles[pageNumber];
      return newPageFiles;
    });
  };

  const handleDrsLevelPodSubmit = async () => {
    try {
      // Get final shipment list (excluding excluded ones)
      const finalShipments = drsShipmentList.filter(
        (shipment) => !excludedShipments.includes(shipment.cAWB_No)
      );

      if (finalShipments.length === 0) {
        message.warning("Please select at least one shipment to update");
        return;
      }

      // Check if all pages have files
      const requiredPages = Array.from({ length: pdfPages }, (_, i) => i + 1);
      const missingPages = requiredPages.filter((page) => !pageFiles[page]);

      if (missingPages.length > 0) {
        message.warning(
          `Please upload files for all pages. Missing: Page ${missingPages.join(
            ", "
          )}`
        );
        return;
      }

      // Create assignments array - group shipments by page
      const assignments = [];

      for (let pageNumber = 1; pageNumber <= pdfPages; pageNumber++) {
        const pageFile = pageFiles[pageNumber];
        if (pageFile) {
          // Get shipments for this page
          const shipmentsForPage = finalShipments
            .filter(
              (_, index) =>
                Math.floor(
                  index / Math.ceil(finalShipments.length / pdfPages)
                ) ===
                pageNumber - 1
            )
            .map((shipment) => shipment.cAWB_No);

          if (shipmentsForPage.length > 0) {
            assignments.push({
              shipments: shipmentsForPage,
              podUrl: pageFile.url,
            });
          }
        }
      }

      // If we have unassigned shipments, assign them to the first page
      const assignedShipments = assignments.flatMap(
        (assignment) => assignment.shipments
      );
      const unassignedShipments = finalShipments
        .map((shipment) => shipment.cAWB_No)
        .filter((awb) => !assignedShipments.includes(awb));

      if (unassignedShipments.length > 0 && assignments.length > 0) {
        assignments[0].shipments.push(...unassignedShipments);
      }

      // Prepare the API payload
      const payload = {
        drsNumber: id,
        drsCreatedDate: drsDetails.createdDate || new Date().toISOString(),
        remarks: "",
        assignments: assignments,
      };

      // Call the new POD update API
      const response = await API.Api(DRS_UPDATE_POD).post(payload);

      if (response.status) {
        message.success(
          response?.response?.data?.message ||
            "DRS Level POD uploaded successfully"
        );
        // Navigate back to DRS details page
        navigate(`/cp-drs-details/${id}`);
      } else {
        console.error("Failed to update POD", response);
        message.error(
          response?.response?.data?.message || "Failed to upload POD"
        );
      }
    } catch (error) {
      console.error("Error submitting DRS level POD:", error);
      message.error("Failed to submit DRS level POD");
    }
  };

  // Helper function to group shipments by pages
  const getShipmentsForPage = (pageNumber) => {
    if (!drsShipmentList.length || !pdfPages) return [];
    
    const shipmentsPerPage = Math.ceil(drsShipmentList.length / pdfPages);
    const startIndex = (pageNumber - 1) * shipmentsPerPage;
    const endIndex = Math.min(startIndex + shipmentsPerPage, drsShipmentList.length);
    
    return drsShipmentList.slice(startIndex, endIndex);
  };



  useEffect(() => {
    if (id) {
      getDrsDetails();
      fetchDrsShipmentList();
    }
  }, [id]);

  return (
    <div className="drs-pod-upload">
      {/* Header Section */}
      <div className="drs-pod-upload-header">
        <Row>
          <Col span={24}>
            <div className="header-content">
              <div className="header-left">
                <Button
                  type="text"
                  icon={<ArrowLeftOutlined />}
                  onClick={() => navigate(`/cp-drs-details/${id}`)}
                  size="large"
                  className="back-button"
                >
                  Back
                </Button>
                <Divider type="vertical" className="header-divider" />
                <Typography.Title level={3} className="header-title">
                  DRS Level POD Upload
                </Typography.Title>
              </div>
            </div>
          </Col>
        </Row>
      </div>

      {/* DRS Information Section */}
      <div className="drs-info-section">
        <Row>
          <Col span={24}>
            <Card
              title="DRS Information"
              style={{ marginBottom: "24px" }}
              size="small"
            >
              <Row gutter={[16, 16]}>
                <Col md={6} xs={12}>
                  <div>
                    <Typography.Text type="secondary">SewaSetu DRS Number</Typography.Text>
                    <br />
                    <Typography.Text strong>{drsDetails.sewaSetuDrsNo || "N/A"}</Typography.Text>
                  </div>
                </Col>
                <Col md={6} xs={12}>
                  <div>
                    <Typography.Text type="secondary">DRS Number</Typography.Text>
                    <br />
                    <Typography.Text strong>{drsDetails.DRS_number || "N/A"}</Typography.Text>
                  </div>
                </Col>
                <Col md={6} xs={12}>
                  <div>
                    <Typography.Text type="secondary">DA Name</Typography.Text>
                    <br />
                    <Typography.Text strong>{drsDetails.createdBy || "N/A"}</Typography.Text>
                  </div>
                </Col>
                <Col md={6} xs={12}>
                  <div>
                    <Typography.Text type="secondary">Phone No.</Typography.Text>
                    <br />
                    <Typography.Text strong>{drsDetails.DAMobileNumber || "N/A"}</Typography.Text>
                  </div>
                </Col>
              </Row>
            </Card>
          </Col>
        </Row>
      </div>

      {/* Main Content */}
      <Row>
        <Col span={24}>
          <Card
            title={
              <div className="card-title-container">
                <span className="card-title-text">
                  Shipments to Update ({drsShipmentList.filter((s) => !excludedShipments.includes(s.cAWB_No)).length})
                </span>
              </div>
            }
            loading={drsLevelLoading}
            className="shipments-card"
          >
            {pdfPages > 0 && (
              <div className="scroll-bar-container">
                <div className="scroll-bar-table">
                  <div className="scroll-bar-header">
                    {Array.from({ length: pdfPages }, (_, index) => {
                      const pageNumber = index + 1;
                      return (
                        <div key={pageNumber} className="page-column-header">
                          Page {pageNumber}
                        </div>
                      );
                    })}
                  </div>
                  
                  <div className="scroll-bar-content">
                    {Array.from({ length: pdfPages }, (_, index) => {
                      const pageNumber = index + 1;
                      const pageShipments = getShipmentsForPage(pageNumber);
                      const pageFile = pageFiles[pageNumber];

                      return (
                        <div key={pageNumber} className="page-column">
                          {/* Shipments List */}
                          <div className="page-shipments-list">
                            {pageShipments.length > 0 ? (
                              pageShipments.map((shipment) => {
                                const isExcluded = excludedShipments.includes(shipment.cAWB_No);
                                return (
                                  <div 
                                    key={shipment.cAWB_No} 
                                    className={`shipment-row ${isExcluded ? 'excluded' : ''}`}
                                  >
                                    <div className="shipment-awb">{shipment.cAWB_No}</div>
                                    <div className="shipment-status">
                                      <Tag 
                                        color={
                                          shipment.status === 'delivered' ? 'green' : 
                                          shipment.status === 'rto_delivered' ? 'orange' :
                                          shipment.status === 'out_for_delivery' ? 'blue' : 'default'
                                        }
                                        size="small"
                                      >
                                        {shipment.status?.replace(/_/g, ' ').toUpperCase()}
                                      </Tag>
                                    </div>
                                  </div>
                                );
                              })
                            ) : (
                              <div className="no-data">No data</div>
                            )}
                          </div>

                          {/* Upload Section */}
                          <div className="page-upload-section">
                            <div className="upload-title">Upload</div>
                            
                            {pageFile ? (
                              <div className="file-uploaded-compact">
                                <div className="file-info-compact">
                                  <div className="file-icon-small">
                                    {pageFile.type === "upload" ? (
                                      <FileImageOutlined />
                                    ) : (
                                      <ScanOutlined />
                                    )}
                                  </div>
                                  <div className="file-name-truncated">{pageFile.name}</div>
                                </div>
                                <Button
                                  type="text"
                                  danger
                                  icon={<DeleteOutlined />}
                                  onClick={() => handleRemovePageFile(pageNumber)}
                                  size="small"
                                />
                              </div>
                            ) : (
                              <div className="upload-placeholder-compact">
                                <div className="upload-area-compact">
                                  <div className="page-number">Page {pageNumber}</div>
                                  
                                  <Upload
                                    accept="image/*,.pdf"
                                    beforeUpload={(file) => {
                                      const isImageOrPdf =
                                        file.type.startsWith("image/") ||
                                        file.type === "application/pdf";
                                      if (!isImageOrPdf) {
                                        message.error("You can only upload image or PDF files!");
                                        return Upload.LIST_IGNORE;
                                      }

                                      const isLt10M = file.size / 1024 / 1024 < 10;
                                      if (!isLt10M) {
                                        message.error("File must be smaller than 10MB!");
                                        return Upload.LIST_IGNORE;
                                      }

                                      handleFileUpload(file, pageNumber);
                                      return false;
                                    }}
                                    showUploadList={false}
                                  >
                                    <Button
                                      icon={<UploadOutlined />}
                                      type="primary"
                                      size="small"
                                      className="upload-button-small"
                                    >
                                      Choose File
                                    </Button>
                                  </Upload>
                                </div>
                              </div>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>
            )}
          </Card>
        </Col>
      </Row>

      {/* Action Buttons */}
      <div className="fixed-footer">
        <Typography.Text>
          {/* {drsShipmentList.filter((s) => !excludedShipments.includes(s.cAWB_No)).length} shipments */}
        </Typography.Text>
        <Space>
          <Button
            onClick={() => navigate(`/cp-drs-details/${id}`)}
            size="large"
          >
            Cancel
          </Button>
          <Button
            type="primary"
            onClick={handleDrsLevelPodSubmit}
            size="large"
            disabled={
              Object.keys(pageFiles).length !== pdfPages ||
              drsShipmentList.filter((s) => !excludedShipments.includes(s.cAWB_No)).length === 0
            }
          >
            Submit POD ({drsShipmentList.filter((s) => !excludedShipments.includes(s.cAWB_No)).length} shipments)
          </Button>
        </Space>
      </div>

      {/* Bottom spacing to account for fixed footer */}
      <div style={{ height: "100px" }}></div>
    </div>
  );
};

export default CpDrsPodUpload;
